"use client";

// 로그인했지만 출생정보가 없는 사용자(주로 구글 가입자)에게 첫 진입 시 생년월일·생시를 받는 온보딩.
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { LocationSearch, type LocationValue } from "../fortune/LocationSearch";
import { Portal } from "./Portal";

const SKIP_KEY = "birth-onboarding-skip"; // 이번 세션 동안만 미루기

export function BirthOnboarding() {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [gender, setGender] = useState<"male" | "female" | "">("");
  const [birthDate, setBirthDate] = useState("");
  const [birthTime, setBirthTime] = useState("");
  const [unknownTime, setUnknownTime] = useState(false);
  const [location, setLocation] = useState<LocationValue | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let skipped = false;
    try {
      skipped = sessionStorage.getItem(SKIP_KEY) === "1";
    } catch {}
    if (skipped) return;
    fetch("/api/auth/me")
      .then((r) => r.json())
      .then((d) => {
        // 로그인 + 출생정보 없음 → 온보딩
        if (d.user && !d.user.birth) setOpen(true);
      })
      .catch(() => {});
  }, []);

  const skip = () => {
    try {
      sessionStorage.setItem(SKIP_KEY, "1");
    } catch {}
    setOpen(false);
  };

  async function submit() {
    setError(null);
    if (!gender) return setError("성별을 선택하세요.");
    if (!birthDate) return setError("생년월일을 입력하세요.");
    if (!unknownTime && !birthTime) return setError("태어난 시간을 입력하거나 '모름'을 선택하세요.");
    if (!location) return setError("태어난 곳을 검색해 선택하세요.");
    const [y, mo, d] = birthDate.split("-").map((n) => parseInt(n, 10));
    const [hh, mm] = (unknownTime ? "12:00" : birthTime).split(":").map((n) => parseInt(n, 10));
    setBusy(true);
    try {
      const res = await fetch("/api/auth/birth", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ gender, year: y, month: mo, day: d, hour: hh, minute: mm, unknownTime, lat: location.lat, lon: location.lon, cityLabel: location.label }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setError(data.message || "저장에 실패했습니다.");
        return;
      }
      setOpen(false);
      router.refresh();
    } catch {
      setError("네트워크 오류가 발생했습니다.");
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "13px 14px",
    borderRadius: 12,
    background: "var(--card-2)",
    border: "1px solid var(--line)",
    color: "var(--ink)",
    fontSize: 15,
    outline: "none",
    boxSizing: "border-box",
  };

  return (
    <Portal>
    <div style={{ position: "fixed", inset: 0, zIndex: 9998, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.6)", backdropFilter: "blur(3px)", padding: 16 }}>
      <div style={{ width: "100%", maxWidth: 400, borderRadius: 20, background: "linear-gradient(var(--card-2), var(--card-2)), var(--bg)", border: "1px solid var(--line-strong)", padding: "26px 22px", boxShadow: "0 16px 50px rgba(0,0,0,0.45)" }}>
        <div style={{ textAlign: "center", marginBottom: 18 }}>
          <div style={{ fontFamily: "'Noto Serif KR',serif", fontSize: 28, color: "var(--gold-bright)" }}>命</div>
          <div style={{ fontFamily: "'Noto Serif KR',serif", fontSize: 19, fontWeight: 600, color: "var(--ink)", marginTop: 6 }}>운명을 읽으려면 출생정보가 필요해요</div>
          <p style={{ fontSize: 13, color: "var(--ink-dim)", lineHeight: 1.6, marginTop: 8 }}>
            사주·점성 해석의 기준이 됩니다.
          </p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", gap: 8 }}>
            {([["male", "남성"], ["female", "여성"]] as const).map(([g, label]) => (
              <button key={g} type="button" onClick={() => setGender(g)}
                style={{ flex: 1, padding: "12px 0", borderRadius: 12, cursor: "pointer", fontSize: 14, fontWeight: 600,
                  background: gender === g ? "var(--accent-soft)" : "var(--card-2)",
                  border: gender === g ? "1px solid var(--line-strong)" : "1px solid var(--line)",
                  color: gender === g ? "var(--gold-bright)" : "var(--ink-dim)" }}>
                {label}
              </button>
            ))}
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, color: "var(--ink-faint)", margin: "0 0 5px 2px" }}>생년월일</label>
            <input type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} min="1900-01-01" max="2025-12-31" style={inputStyle} />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, color: "var(--ink-faint)", margin: "0 0 5px 2px" }}>태어난 시간</label>
            <input type="time" value={birthTime} onChange={(e) => setBirthTime(e.target.value)} disabled={unknownTime} style={{ ...inputStyle, opacity: unknownTime ? 0.5 : 1 }} />
            <label style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 8, fontSize: 13, color: "var(--ink-dim)", cursor: "pointer" }}>
              <input type="checkbox" checked={unknownTime} onChange={(e) => setUnknownTime(e.target.checked)} style={{ accentColor: "var(--gold)" }} />
              태어난 시간을 몰라요
            </label>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, color: "var(--ink-faint)", margin: "0 0 5px 2px" }}>태어난 곳</label>
            <LocationSearch value={location ?? { lat: NaN, lon: NaN, label: "" }} onChange={setLocation} />
          </div>

          {error && <div style={{ fontSize: 13, color: "var(--rose)", lineHeight: 1.5 }}>{error}</div>}

          <button onClick={submit} disabled={busy}
            style={{ marginTop: 4, padding: "13px 0", borderRadius: 12, border: "none", cursor: busy ? "default" : "pointer", background: "var(--btn-grad)", color: "var(--btn-ink)", fontSize: 15, fontWeight: 700, opacity: busy ? 0.6 : 1 }}>
            {busy ? "저장 중…" : "저장하고 시작하기"}
          </button>
          <button onClick={skip} style={{ padding: "9px 0", borderRadius: 12, border: "none", cursor: "pointer", background: "transparent", color: "var(--ink-faint)", fontSize: 13 }}>
            나중에 입력할게요
          </button>
        </div>
      </div>
    </div>
    </Portal>
  );
}
